import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Flask, Atom, Download, CheckCircle, XCircle, Copy, MagnifyingGlass, Pill, BookOpen, FilePdf, Table, CaretDown, CaretRight, User, IdentificationCard, Folder, FileText, VideoCamera, FileArrowUp } from '@phosphor-icons/react';
import SmilesDrawer from 'smiles-drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ✨ Data is imported from the separate data file to keep this component fast
import { PORTAL_SUBJECTS, PORTAL_UNITS, PORTAL_CHAPTERS, CHAPTER_NOTES, SYLLABUS_DATA } from '../data/courseData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const getFallbackNotes = (chapter, subject) => `
    <h3 class="text-xl font-bold text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">${chapter || 'Topic'}</h3>
    <p class="text-slate-700 leading-relaxed mb-4">Detailed theoretical notes, structural classifications, and SAR mechanisms for <b>${chapter || 'this topic'}</b> (${subject || 'Subject'}) are being updated according to the latest PCI guidelines.</p>
    <ul class="list-disc pl-6 space-y-2 text-slate-700">
        <li>Historical Background and nomenclature.</li>
        <li>Pharmacological mechanisms and target enzymes.</li>
        <li>Stereochemical requirements for biological activity.</li>
        <li>Structure-Activity Relationship (SAR) mapping.</li>
        <li>Chemical degradation and metabolic pathways.</li>
    </ul>
`;

const VirtualLab = () => {
  const [view, setView] = useState('lab'); 
  const [portal, setPortal] = useState({ category: '', sem: null, subject: null, unit: null, chapter: null });

  const [inputValue, setInputValue] = useState('');
  const [inputType, setInputType] = useState('name');
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [searchedName, setSearchedName] = useState('');
  const [jsmeReady, setJsmeReady] = useState(false);
  
  // ==========================================
  // ✨ NEW STATE FOR MOA (MODE OF ACTION)
  // ==========================================
  const [moaData, setMoaData] = useState(null);
  const [isMoaLoading, setIsMoaLoading] = useState(false);

  const [syllabusOpen, setSyllabusOpen] = useState(false);
  const [obsOpen, setObsOpen] = useState(false);
  const [studentName, setStudentName] = useState('');
  const [studentRoll, setStudentRoll] = useState('');

  const smiles2DRef = useRef(null);
  const resultsRef = useRef(null);
  const smilesDrawer = useRef(null);
  const jsmeAppletRef = useRef(null);

  useEffect(() => {
    if (!smilesDrawer.current) { smilesDrawer.current = new SmilesDrawer.Drawer({ width: 300, height: 300, bondThickness: 1.5 }); }
  }, []);

  useEffect(() => {
    window.jsmeOnLoad = () => { setJsmeReady(true); };
    if (window.JSApplet && window.JSApplet.JSME) { setJsmeReady(true); }
    return () => { delete window.jsmeOnLoad; };
  }, []);

  const initJsmeEditor = () => {
    const container = document.getElementById('jsme_container');
    if (!container || !window.JSApplet || !jsmeReady) return;
    try {
      jsmeAppletRef.current = null;
      jsmeAppletRef.current = new window.JSApplet.JSME('jsme_container', '100%', '450px', { options: 'oldlook,star' });
    } catch (e) { console.error('JSME init error:', e); }
  };

  useEffect(() => {
    if (view === 'lab' && inputType === 'draw' && jsmeReady) { setTimeout(initJsmeEditor, 150); }
  }, [inputType, jsmeReady, view]);

  const openPortal = (category, sem) => {
    setView('portal');
    setPortal({ category, sem, subject: null, unit: null, chapter: null });
  };

  const handleGetSmilesFromDrawing = () => {
    if (!jsmeAppletRef.current) return toast.error('Drawing editor not ready yet');
    try {
      const smiles = jsmeAppletRef.current.smiles();
      if (!smiles || smiles.trim() === '') return toast.error('Please draw a molecular structure first');
      setInputValue(smiles); setInputType('smiles'); handleAnalyze(smiles); 
      toast.success('Structure captured! Analyzing...');
    } catch (e) { toast.error('Could not read structure from editor'); }
  };

  const draw2DStructure = (smiles) => {
    if (smiles2DRef.current && smilesDrawer.current) {
      SmilesDrawer.parse(smiles, (tree) => { smilesDrawer.current.draw(tree, smiles2DRef.current, 'light', false); });
    }
  };

  // ==========================================
  // ✨ FETCH MOA FROM BACKEND (RENDER)
  // ==========================================
  const fetchDrugMOA = async (drugName) => {
      console.log("🚀 MOA Analysis Triggered for:", drugName);
      setIsMoaLoading(true);
      setMoaData(null);
      try {
          const response = await fetch('https://chem-lab-backend-nfno.onrender.com/api/get-moa', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ drug_name: drugName })
          });
          
          const data = await response.json();
          if (data.moa) {
              setMoaData(data.moa);
              console.log("✅ MOA Data Received!");
          }
      } catch (error) {
          console.error("❌ Error fetching MOA:", error);
      } finally {
          setIsMoaLoading(false);
      }
  };

  const handleAnalyze = async (overrideValue = null) => {
    const query = overrideValue || inputValue;
    if (!query.trim()) return toast.error('Please enter a drug name or SMILES string');
    if (overrideValue) { setInputValue(overrideValue); setInputType('name'); }
    setSearchedName(query);
    setLoading(true); setAnalysisData(null);
    setMoaData(null); 
    
    try {
      let smiles = query; let compoundName = query;
      if (inputType === 'name' || overrideValue) {
        const lookupResponse = await axios.post(`${API}/lookup`, { query: query });
        smiles = lookupResponse.data.smiles; compoundName = lookupResponse.data.generic_name;
      }
      const analysisResponse = await axios.post(`${API}/analyze`, { smiles: smiles, compound_name: compoundName });
      
      setAnalysisData(analysisResponse.data);
      
      // ✨ FIX: Here we are sending the original simple "query" (e.g. Erythromycin) 
      // instead of the complex IUPAC compoundName to Gemini.
      fetchDrugMOA(query);

      setTimeout(() => draw2DStructure(analysisResponse.data.smiles), 100);
      toast.success('Analysis completed successfully');
    } catch (error) {
      toast.error('Analysis failed.');
    } finally { setLoading(false); }
  };

  const handleCopySmiles = async () => {
    if (!analysisData?.smiles) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(analysisData.smiles); toast.success('SMILES copied'); return; }
      throw new Error('Clipboard API unavailable');
    } catch (err) {
      try {
        const textArea = document.createElement('textarea'); textArea.value = analysisData.smiles;
        textArea.style.position = 'fixed'; textArea.style.opacity = '0'; document.body.appendChild(textArea);
        textArea.select(); document.execCommand('copy'); document.body.removeChild(textArea); toast.success('SMILES copied');
      } catch (fallbackErr) { toast.error('Failed to copy.'); }
    }
  };

  const handleExportPDF = async () => {
    if (!resultsRef.current || !analysisData) return toast.error('No analysis data to export');
    toast.info('Generating PDF...');
    try {
      const canvas = await html2canvas(resultsRef.current, { scale: 2, backgroundColor: '#FFFFFF' });
      const imgData = canvas.toDataURL('image/png'); const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth(); const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight); pdf.save(`${analysisData.generic_name?.replace(/\s/g, '_') || 'drug'}_analysis.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) { toast.error('Failed to export PDF'); }
  };

  const generateObservationPDF = async () => {
    if (!analysisData) return toast.error('Please analyze a compound first');
    toast.info('Generating Observation Sheet...');
    try {
      let structureDataUrl = null;
      if (smiles2DRef.current) structureDataUrl = smiles2DRef.current.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4'); const pageWidth = pdf.internal.pageSize.getWidth(); const marginX = 15; let y = 15;
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14); pdf.text('BP607P - MEDICINAL CHEMISTRY III', pageWidth / 2, y, { align: 'center' });
      y += 6; pdf.setFontSize(11); pdf.text('PRACTICAL OBSERVATION SHEET', pageWidth / 2, y, { align: 'center' });
      y += 4; pdf.setDrawColor(30, 64, 175); pdf.setLineWidth(0.5); pdf.line(marginX, y, pageWidth - marginX, y); y += 8;
      
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); const dateStr = new Date().toLocaleDateString('en-GB');
      pdf.text(`Name: ${studentName || '_______________________'}`, marginX, y); pdf.text(`Roll No.: ${studentRoll || '_______________'}`, pageWidth / 2 + 10, y);
      y += 6; pdf.text(`Date: ${dateStr}`, marginX, y); y += 10;
      
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.text('AIM', marginX, y); y += 5;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
      const displayDrugName = searchedName || analysisData.generic_name || 'Compound';
      const aimText = `To draw the chemical structure of ${displayDrugName}, calculate its physicochemical properties, and evaluate its drug-likeness.`;
      const aimLines = pdf.splitTextToSize(aimText, pageWidth - 2 * marginX); pdf.text(aimLines, marginX, y); y += aimLines.length * 4.5 + 5;
      
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.text('CHEMICAL IDENTIFICATION', marginX, y); y += 5;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.text(`Generic Name: ${displayDrugName}`, marginX, y); y += 5;
      
      if (structureDataUrl) {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.text('STRUCTURE', marginX, y); y += 5;
        try { pdf.addImage(structureDataUrl, 'PNG', marginX + 30, y, 120, 80); y += 85; } catch (e) { y += 5; }
      }
      
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.text('PHYSICOCHEMICAL PROPERTIES', marginX, y); y += 6;
      const props = analysisData.properties || {};
      const tableRows = [['Property', 'Value', 'Unit'], ['MW (Da)', (props.molecular_weight||0).toString(), 'Da'], ['log P', (props.log_p||0).toString(), '-'], ['H-Bond Donors', (props.hbd||0).toString(), '-'], ['H-Bond Acceptors', (props.hba||0).toString(), '-'], ['Molar Refractivity', (props.molar_refractivity||0).toString(), 'cm³/mol']];
      const colWidths = [80, 50, 40]; const rowHeight = 7;
      tableRows.forEach((row, i) => {
        pdf.setFont('helvetica', i === 0 ? 'bold' : 'normal'); pdf.setFontSize(9); let x = marginX;
        row.forEach((cell, j) => { pdf.rect(x, y, colWidths[j], rowHeight); pdf.text(cell, x + 2, y + 5); x += colWidths[j]; });
        y += rowHeight;
      }); y += 6;
      
      if (y > 240) { pdf.addPage(); y = 15; }
      
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.text("LIPINSKI'S RULE OF 5", marginX, y); y += 6;
      const lip = analysisData.lipinski || {};
      const ro5Rows = [['Criterion', 'Observed', 'Status'], ['MW <= 500', `${props.molecular_weight||0} Da`, lip.mw_pass ? 'PASS' : 'FAIL'], ['log P <= 5', (props.log_p||0).toString(), lip.log_p_pass ? 'PASS' : 'FAIL'], ['HBD <= 5', (props.hbd||0).toString(), lip.hbd_pass ? 'PASS' : 'FAIL'], ['HBA <= 10', (props.hba||0).toString(), lip.hba_pass ? 'PASS' : 'FAIL']];
      const ro5ColWidths = [60, 50, 40];
      ro5Rows.forEach((row, i) => {
        pdf.setFont('helvetica', i === 0 ? 'bold' : 'normal'); pdf.setFontSize(9); let x = marginX;
        row.forEach((cell, j) => { pdf.rect(x, y, ro5ColWidths[j], rowHeight); pdf.text(cell, x + 2, y + 5); x += ro5ColWidths[j]; });
        y += rowHeight;
      }); y += 4;
      
      pdf.setFont('helvetica', 'bold'); pdf.text(`Violations: ${lip.violations||0}`, marginX, y); y += 8;
      
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.text('CONCLUSION', marginX, y); y += 6;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
      const conclusionText = `Based on the analysis, ${displayDrugName} shows ${lip.violations||0} violation(s) of Lipinski's Rule. ${lip.verdict||''}`;
      const concLines = pdf.splitTextToSize(conclusionText, pageWidth - 2 * marginX); pdf.text(concLines, marginX, y); y += concLines.length * 4.5 + 15;
      
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
      pdf.text("Student's Signature: ____________________", marginX, y); pdf.text("Faculty's Signature: ____________________", pageWidth - marginX - 70, y);
      
      pdf.save(`Observation_${studentRoll || displayDrugName}.pdf`);
      setObsOpen(false); toast.success('Observation Sheet downloaded successfully!');
    } catch (error) { toast.error('Failed to generate observation sheet'); }
  };

  const handlePickSyllabusDrug = (drugName) => {
    setSyllabusOpen(false);
    setView('lab'); 
    setInputValue(drugName);
    setInputType('name');
    toast.info(`Fetching data for ${drugName}...`);
    setTimeout(() => { handleAnalyze(drugName); }, 400);
  };

  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      
      {/* --- 1. TOP NAVIGATION BAR --- */}
      <div className="bg-blue-900 text-white shadow-md relative z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-2 flex justify-between items-center">
            <div className="font-bold tracking-widest text-xs uppercase cursor-pointer" onClick={() => setView('lab')}>Drugpropcal.online</div>
            <nav className="flex space-x-6 text-sm font-bold items-center">
                <button onClick={() => setView('lab')} className="hover:text-blue-200 transition-colors">Home</button>
                
                <div className="relative group py-2">
                    <button className="hover:text-blue-200 transition-colors flex items-center gap-1 cursor-pointer">
                        B.Pharm Notes <CaretDown size={14} weight="bold" />
                    </button>
                    <div className="absolute left-0 top-full mt-1 w-44 bg-white rounded-md shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-in-out transform translate-y-2 group-hover:translate-y-0 border border-slate-200 z-50">
                        {semesters.map((sem) => (
                            <button 
                                key={`Notes-${sem}`} 
                                onClick={() => openPortal('Notes', sem)} 
                                className="block w-full text-left px-4 py-2 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                            >
                                Semester {sem}
                            </button>
                        ))}
                    </div>
                </div>
                
                <a href="/course-module1.html" className="text-yellow-300 font-bold hover:text-yellow-100 transition-colors flex items-center gap-1">Free Course 🎓</a>
            </nav>
        </div>
      </div>

      <main className="flex-grow">
          {view === 'portal' ? (
            <div className="max-w-[1200px] mx-auto py-8 px-6 animate-fadeIn">
                <div className="bg-white px-6 py-4 rounded-xl shadow-sm border border-slate-300 mb-6 flex flex-wrap items-center text-sm font-bold text-slate-600 gap-2">
                    <button onClick={() => setView('lab')} className="hover:text-blue-700 transition-colors">Home</button> <CaretRight size={14}/>
                    <button onClick={() => setPortal({...portal, subject: null, unit: null, chapter: null})} className="hover:text-blue-700 transition-colors">{portal.category}</button> <CaretRight size={14}/>
                    <button onClick={() => setPortal({...portal, subject: null, unit: null, chapter: null})} className="hover:text-blue-700 transition-colors">Semester {portal.sem}</button> 
                    {portal.subject && <><CaretRight size={14}/> <button onClick={() => setPortal({...portal, unit: null, chapter: null})} className="hover:text-blue-700 text-blue-700 transition-colors">{portal.subject?.id}</button></>}
                    {portal.unit && <><CaretRight size={14}/> <button onClick={() => setPortal({...portal, unit: null, chapter: null})} className="hover:text-blue-700 text-blue-700 transition-colors">{portal.unit}</button></>}
                    {portal.chapter && <><CaretRight size={14}/> <span className="text-blue-900 truncate max-w-[250px]">{portal.chapter}</span></>}
                </div>

                <div className="bg-white rounded-xl shadow-md border border-slate-300 min-h-[600px] overflow-hidden">
                    <div className="bg-slate-50 border-b border-slate-200 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-2xl md:text-3xl font-black text-slate-800 tracking-tight">
                                {portal.chapter ? portal.chapter : portal.unit ? `${portal.subject?.id} - ${portal.unit}` : portal.subject ? portal.subject?.name : `${portal.category} (Semester ${portal.sem})`}
                            </h2>
                        </div>
                    </div>

                    <div className="p-6 md:p-10 bg-white">
                        {!portal.subject && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fadeIn">
                                {(PORTAL_SUBJECTS[portal.sem] || PORTAL_SUBJECTS.default).map((sub) => (
                                    <button key={sub.id} onClick={() => setPortal({...portal, subject: sub})} className="text-left bg-white border-2 border-slate-100 hover:border-blue-500 p-6 rounded-xl transition-all shadow-sm hover:shadow-lg group">
                                        <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-600 transition-colors">
                                            <Folder size={28} weight="fill" className="text-blue-600 group-hover:text-white" />
                                        </div>
                                        <h3 className="font-black text-xl text-slate-800">{sub.id}</h3>
                                        <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">{sub.name}</p>
                                    </button>
                                ))}
                            </div>
                        )}

                        {portal.subject && !portal.unit && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 animate-fadeIn">
                                {PORTAL_UNITS.map((u) => (
                                    <button key={u} onClick={() => setPortal({...portal, unit: u})} className="text-center bg-slate-50 border-2 border-slate-200 hover:bg-blue-50 hover:border-blue-500 p-6 rounded-xl transition-all shadow-sm hover:shadow-md group">
                                        <div className="mx-auto bg-white border border-slate-200 w-12 h-12 rounded-full flex items-center justify-center mb-3 group-hover:border-blue-400 transition-colors">
                                            <span className="font-black text-slate-400 group-hover:text-blue-600 transition-colors">{u.replace('Unit ', '')}</span>
                                        </div>
                                        <span className="font-bold text-slate-700 group-hover:text-blue-800 transition-colors">{u}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {portal.unit && !portal.chapter && (
                            <div className="space-y-3 max-w-4xl mx-auto animate-fadeIn">
                                {((PORTAL_CHAPTERS[portal.subject?.id] && PORTAL_CHAPTERS[portal.subject?.id][portal.unit]) || ["Syllabus Material", "Study Notes", "Previous Year Papers"]).map((chap, idx) => (
                                    <button key={idx} onClick={() => setPortal({...portal, chapter: chap})} className="w-full text-left bg-white border-2 border-slate-100 p-5 rounded-xl hover:shadow-md hover:border-blue-400 transition-all flex items-center gap-5 group">
                                        <div className="bg-slate-100 p-3 rounded-lg text-slate-500 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors">
                                            {portal.category === 'Video Lectures' ? <VideoCamera size={24} weight="fill"/> : portal.category === 'Question Papers' ? <FileArrowUp size={24} weight="fill"/> : <FileText size={24} weight="fill"/>}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-bold text-lg text-slate-800 group-hover:text-blue-900 transition-colors">{chap}</h4>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1">{portal.subject?.id} • {portal.category}</p>
                                        </div>
                                        <CaretRight size={20} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
                                    </button>
                                ))}
                            </div>
                        )}

                        {portal.chapter && (
                            <div 
                                onContextMenu={(e) => { e.preventDefault(); }}
                                onCopy={(e) => { e.preventDefault(); }}
                                onCut={(e) => { e.preventDefault(); }}
                                onDrag={(e) => { e.preventDefault(); }}
                                onSelectStart={(e) => { e.preventDefault(); }}
                                onKeyDown={(e) => {
                                    if (e.ctrlKey && (e.key === 'c' || e.key === 'p' || e.key === 's' || e.key === 'u' || e.key === 'a' || e.key === 'x')) {
                                        e.preventDefault();
                                    }
                                }}
                                className="relative p-8 md:p-12 bg-white border border-slate-200 rounded-xl min-h-[500px] overflow-hidden animate-fadeIn"
                                style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}
                            >
                                <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] text-6xl md:text-8xl font-black rotate-[-30deg] pointer-events-none whitespace-nowrap overflow-hidden z-0">
                                    DRUGPROPCAL.ONLINE
                                </div>
                                <div className="relative z-10 font-serif text-slate-800">
                                    <div 
                                        className="prose max-w-none text-justify leading-loose text-[15.5px]"
                                        dangerouslySetInnerHTML={{ __html: CHAPTER_NOTES[portal.chapter] || getFallbackNotes(portal.chapter, portal.subject?.id) }} 
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

          ) : (

            <div className="bg-slate-200 pb-12">
                <header className="bg-white border-b border-slate-300">
                    <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Flask size={32} weight="fill" className="text-blue-700" />
                        <div>
                        <h1 className="text-2xl font-black font-logo text-slate-900 tracking-tight">Virtual Lab Assistant</h1>
                        <p className="text-xs text-slate-600 font-heading uppercase tracking-wider">Computational Chemistry • BP607P Practical</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {analysisData && (
                            <>
                                <Button onClick={() => setSyllabusOpen(true)} variant="outline" className="border-blue-700 text-blue-700 hover:bg-blue-50 font-bold shadow-sm">
                                    <BookOpen size={18} className="mr-2" weight="bold" /> Syllabus Mode
                                </Button>
                                <Button onClick={() => setObsOpen(true)} className="bg-green-700 hover:bg-green-800 text-white font-bold shadow-sm">
                                    <FilePdf size={18} className="mr-2" weight="bold" /> Observation Sheet
                                </Button>
                                <Button onClick={handleExportPDF} className="bg-blue-700 hover:bg-blue-800 text-white font-bold shadow-sm">
                                    <Download size={18} className="mr-2" weight="bold" /> Export PDF
                                </Button>
                            </>
                        )}
                        {!analysisData && (
                            <Button onClick={() => setSyllabusOpen(true)} variant="outline" className="border-blue-700 text-blue-700 hover:bg-blue-50 font-bold shadow-sm">
                                <BookOpen size={18} className="mr-2" weight="bold" /> Syllabus Mode
                            </Button>
                        )}
                    </div>
                    </div>
                </header>

                <div className="max-w-[1400px] mx-auto p-1 gap-1 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 mt-1 animate-fadeIn">
                    <div className="bg-white p-6 border border-slate-300 flex flex-col">
                    <h2 className="text-xl font-bold font-heading text-slate-900 mb-4 flex items-center gap-2">
                        <Atom size={24} weight="fill" className="text-blue-700" /> Compound Input
                    </h2>
                    <Tabs value={inputType} onValueChange={setInputType} className="mb-4">
                        <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="name">Drug Name</TabsTrigger>
                        <TabsTrigger value="smiles">SMILES</TabsTrigger>
                        <TabsTrigger value="draw">Draw</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="name" className="mt-4 min-h-[150px]">
                        <Label className="text-xs uppercase tracking-wider font-medium text-slate-600">Generic Drug Name</Label>
                        <Input placeholder="e.g., Penicillin, Isoniazid" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()} className="mt-2" />
                        <div className="mt-3 flex flex-wrap gap-2">
                            <span className="text-[10px] uppercase font-bold text-slate-400 mt-1">Try:</span>
                            <button onClick={() => handleAnalyze('Penicillin')} className="text-[11px] font-medium bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-700 px-2 py-1 rounded border border-blue-200 transition-colors shadow-sm">Penicillin</button>
                            <button onClick={() => handleAnalyze('Isoniazid')} className="text-[11px] font-medium bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-700 px-2 py-1 rounded border border-blue-200 transition-colors shadow-sm">Isoniazid</button>
                            <button onClick={() => handleAnalyze('Erythromycin')} className="text-[11px] font-medium bg-blue-50 hover:bg-blue-600 hover:text-white text-blue-700 px-2 py-1 rounded border border-blue-200 transition-colors shadow-sm">Erythromycin</button>
                        </div>
                        </TabsContent>
                        
                        <TabsContent value="smiles" className="mt-4 min-h-[150px]">
                        <Label className="text-xs uppercase tracking-wider font-medium text-slate-600">SMILES String</Label>
                        <Input placeholder="CC(=O)Oc1ccccc1C(=O)O" value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()} className="mt-2 font-mono text-sm" />
                        </TabsContent>

                        <TabsContent value="draw" className="mt-4 min-h-[150px]">
                        <Label className="text-xs uppercase tracking-wider font-medium text-slate-600 mb-2 block">Draw Molecule</Label>
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded text-blue-800 text-xs font-medium leading-relaxed">
                            ✏️ <strong>निर्देश:</strong> बीच वाले बड़े पैनल में JSME Editor ओपन हो गया है। वहाँ अपना स्ट्रक्चर ड्रा करें और नीचे दिए गए बटन पर क्लिक करें।
                        </div>
                        </TabsContent>
                    </Tabs>

                    {inputType !== 'draw' && (
                        <Button onClick={() => handleAnalyze()} disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800 text-white font-bold mt-2 h-11 shadow-md">
                        {loading ? 'Analyzing...' : <><MagnifyingGlass size={18} className="mr-2" weight="bold" /> Analyze Compound</>}
                        </Button>
                    )}

                    {analysisData && (
                        <div className="mt-6 pt-6 border-t border-slate-200">
                        <p className="text-xs uppercase tracking-wider font-medium text-slate-600 mb-2">Identification</p>
                        <p className="text-sm font-bold text-slate-900 mb-2">{analysisData.generic_name}</p>
                        <div className="flex items-start gap-2">
                            <code className="flex-1 text-xs font-mono text-slate-700 bg-slate-100 p-2 break-all border border-slate-200 rounded">{analysisData.smiles}</code>
                            <Button variant="outline" size="icon" onClick={handleCopySmiles} className="shrink-0"><Copy size={16} /></Button>
                        </div>
                        </div>
                    )}
                    </div>

                    <div className="bg-white p-6 border border-slate-300 md:col-span-2 flex flex-col">
                    <h2 className="text-xl font-bold font-heading text-slate-900 mb-4">
                        {inputType === 'draw' ? 'Draw Molecular Structure' : 'Molecular Structure (2D)'}
                    </h2>
                    
                    <div className="flex-1 flex flex-col relative min-h-[450px]">
                        <div className={`flex-1 items-center justify-center w-full h-full ${inputType === 'draw' ? 'hidden' : 'flex'}`}>
                        {!analysisData && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300">
                            <Atom size={64} weight="light" className="mb-3" />
                            <p className="text-sm font-medium text-slate-400">Structure will appear after analysis</p>
                            </div>
                        )}
                        <canvas ref={smiles2DRef} width="500" height="400" className="border border-slate-200 bg-white" style={{ display: analysisData ? 'block' : 'none' }} />
                        </div>

                        {inputType === 'draw' && (
                        <div className="flex-1 flex flex-col w-full h-full animate-fadeIn">
                            <div className="bg-slate-50 p-2 border border-slate-300 rounded shadow-inner flex-1 flex flex-col" style={{ minHeight: '450px' }}>
                            <div id="jsme_container" style={{ width: '100%', height: '100%', flex: 1 }} />
                            </div>
                            <Button onClick={handleGetSmilesFromDrawing} disabled={!jsmeReady} className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold mt-4 h-12 text-[15px] shadow-md">
                            ✏️ Capture Structure & Run Auto-Analysis
                            </Button>
                        </div>
                        )}
                    </div>
                    </div>

                    <div className="bg-white p-6 border border-slate-300 flex flex-col" ref={resultsRef}>
                    <h2 className="text-xl font-bold font-heading text-slate-900 mb-4">Properties & RO5</h2>
                    {analysisData ? (
                        <div className="flex-1">
                        <table className="w-full text-sm">
                            <tbody>
                            <tr className="border-b border-slate-200"><td className="py-2 font-medium text-slate-600">MW (Da)</td><td className="py-2 text-right font-mono font-semibold text-slate-900">{analysisData.properties?.molecular_weight}</td></tr>
                            <tr className="border-b border-slate-200"><td className="py-2 font-medium text-slate-600">log P</td><td className="py-2 text-right font-mono font-semibold text-slate-900">{analysisData.properties?.log_p}</td></tr>
                            <tr className="border-b border-slate-200"><td className="py-2 font-medium text-slate-600">HBD</td><td className="py-2 text-right font-mono font-semibold text-slate-900">{analysisData.properties?.hbd}</td></tr>
                            <tr className="border-b border-slate-200"><td className="py-2 font-medium text-slate-600">HBA</td><td className="py-2 text-right font-mono font-semibold text-slate-900">{analysisData.properties?.hba}</td></tr>
                            <tr><td className="py-2 font-medium text-slate-600">MR</td><td className="py-2 text-right font-mono font-semibold text-slate-900">{analysisData.properties?.molar_refractivity}</td></tr>
                            </tbody>
                        </table>

                        <div className="mt-5 pt-5 border-t border-slate-200">
                            <h3 className="text-[15px] font-bold font-heading text-slate-900 mb-3">Lipinski Screening</h3>
                            <div className="space-y-1.5 text-xs mb-3">
                            <div className="flex items-center justify-between"><span className="text-slate-600">MW ≤ 500</span>{analysisData.lipinski?.mw_pass ? <CheckCircle size={16} weight="fill" className="text-green-600" /> : <XCircle size={16} weight="fill" className="text-red-600" />}</div>
                            <div className="flex items-center justify-between"><span className="text-slate-600">log P ≤ 5</span>{analysisData.lipinski?.log_p_pass ? <CheckCircle size={16} weight="fill" className="text-green-600" /> : <XCircle size={16} weight="fill" className="text-red-600" />}</div>
                            <div className="flex items-center justify-between"><span className="text-slate-600">HBD ≤ 5</span>{analysisData.lipinski?.hbd_pass ? <CheckCircle size={16} weight="fill" className="text-green-600" /> : <XCircle size={16} weight="fill" className="text-red-600" />}</div>
                            <div className="flex items-center justify-between"><span className="text-slate-600">HBA ≤ 10</span>{analysisData.lipinski?.hba_pass ? <CheckCircle size={16} weight="fill" className="text-green-600" /> : <XCircle size={16} weight="fill" className="text-red-600" />}</div>
                            </div>
                            <div className="bg-slate-100 p-3 border border-slate-300 rounded shadow-inner mt-4">
                            <p className="text-xs uppercase tracking-wider font-medium text-slate-600 mb-1">Violations</p>
                            <p className="text-2xl font-black font-mono text-slate-900">{analysisData.lipinski?.violations}</p>
                            </div>
                        </div>

                        {/* ========================================== */}
                        {/* ✨ NEW UI CARD FOR MODE OF ACTION (MOA)    */}
                        {/* ========================================== */}
                        <div className="mt-6">
                            {(isMoaLoading || moaData) && (
                                <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-5 shadow-sm">
                                    <h3 className="text-[15px] font-bold text-indigo-900 mb-4 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                        </svg>
                                        Mode of Action (MOA)
                                    </h3>
                                    
                                    {isMoaLoading ? (
                                        <div className="flex items-center space-x-2 text-indigo-600">
                                            <div className="w-4 h-4 rounded-full animate-pulse bg-indigo-600"></div>
                                            <div className="w-4 h-4 rounded-full animate-pulse bg-indigo-600" style={{ animationDelay: '200ms' }}></div>
                                            <div className="w-4 h-4 rounded-full animate-pulse bg-indigo-600" style={{ animationDelay: '400ms' }}></div>
                                            <span className="ml-2 text-sm font-medium">Generating mechanism...</span>
                                        </div>
                                    ) : (
                                        <div 
                                            className="text-[13px] leading-relaxed prose max-w-none text-slate-800"
                                            dangerouslySetInnerHTML={{ __html: moaData }} 
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        {/* ========================================== */}

                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 py-12">
                        <Table size={48} weight="light" className="mb-3 opacity-30" /><p className="text-sm">Data will appear here</p>
                        </div>
                    )}
                    </div>
                </div>
            </div>
          )}
      </main>

      {/* --- FOOTER --- */}
      <footer className="mt-auto bg-white border-t border-slate-300 pt-10 pb-6 px-6 w-full">
        <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left mb-8 max-w-4xl mx-auto">
                <div>
                    <h4 className="font-bold text-slate-900 mb-4 text-lg border-b border-slate-200 pb-2">Academic Links</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm text-blue-700 font-medium">
                        <a href="/video-lectures.html" className="hover:text-blue-900 hover:underline">Video Lectures</a>
                        <a href="/bpharm-gpat.html" className="hover:text-blue-900 hover:underline">GPAT Papers</a>
                        <a href="/bpharm-career.html" className="hover:text-blue-900 hover:underline">Career Options</a>
                        <a href="/dpharm.html" className="hover:text-blue-900 hover:underline">D.Pharm Notes</a>
                        <a href="/mpharm.html" className="hover:text-blue-900 hover:underline">M.Pharm Notes</a>
                        <a href="/cadd-basics.html" className="hover:text-blue-900 hover:underline">CADD Course</a>
                    </div>
                </div>
                <div>
                    <h4 className="font-bold text-slate-900 mb-4 text-lg border-b border-slate-200 pb-2">General Links</h4>
                    <div className="flex flex-col gap-3 text-sm text-blue-700 font-medium">
                        <a href="/about.html" className="hover:text-blue-900 hover:underline">About Us</a>
                        <a href="/privacy.html" className="hover:text-blue-900 hover:underline">Privacy Policy</a>
                        <a href="/contact.html" className="hover:text-blue-900 hover:underline">Contact Us</a>
                        <a href="/blog.html" className="hover:text-blue-900 hover:underline">Blog</a>
                    </div>
                </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 max-w-4xl mx-auto">
                <p className="text-[11px] text-red-700 font-bold uppercase tracking-wide leading-relaxed text-center">
                    <span className="text-red-600 text-sm mr-1">⚠️</span> Disclaimer: This tool is strictly for academic and educational purposes. Data is computationally generated and must not be used for medical diagnosis.
                </p>
            </div>
            
            <p className="text-sm font-bold text-slate-900 font-heading text-center">© 2026 Drugpropcal.online | Prof. Satyendra Singh Rathore</p>
        </div>
      </footer>

      {/* --- DIALOGS --- */}
      <Dialog open={obsOpen} onOpenChange={setObsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-900 flex items-center gap-2 font-black">
              <FilePdf size={24} weight="fill" /> Print Observation Sheet
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Student Name</Label>
              <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-10" placeholder="Enter your full name" value={studentName} onChange={(e) => setStudentName(e.target.value)} />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Roll Number</Label>
              <div className="relative">
                  <IdentificationCard size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-10" placeholder="Enter your roll number" value={studentRoll} onChange={(e) => setStudentRoll(e.target.value)} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={generateObservationPDF} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold h-11 text-[15px] shadow-md">
              <Download size={18} className="mr-2" weight="bold" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={syllabusOpen} onOpenChange={setSyllabusOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
                <BookOpen size={22} weight="fill" className="text-blue-700" /> BP601T Syllabus Mode (Unit-wise)
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 space-y-6">
            {SYLLABUS_DATA.map((unitData, uIdx) => (
              <div key={uIdx} className="border border-slate-300 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-blue-900 text-white px-4 py-2 font-bold text-sm tracking-wide">
                  {unitData.unit}
                </div>
                <div className="p-4 bg-slate-50 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {unitData.chapters.map((chapter, cIdx) => (
                    <div key={cIdx} className="bg-white border border-slate-200 p-3 rounded">
                      <h3 className="font-bold text-[13px] uppercase text-blue-800 mb-2 border-b border-slate-100 pb-1 flex items-center gap-1">
                          <Pill size={14} weight="fill" /> {chapter.name}
                      </h3>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {chapter.drugs.map((drug) => (
                          <button 
                            key={drug} 
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePickSyllabusDrug(drug);
                            }} 
                            className="text-[11px] font-mono font-medium text-slate-700 bg-slate-100 hover:bg-blue-700 hover:text-white border border-slate-300 px-2 py-1 rounded transition-colors"
                          >
                            {drug}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
      `}} />
    </div>
  );
};

export default VirtualLab;